import { useId, useState } from 'react';
import { Box, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DocumentTextIcon, XMarkIcon } from '@heroicons/react/24/outline';

import useAnalytics from '@/hooks/useAnalytics';
import { primitiveColors, semanticColors } from '@/theme/tokens';

import SourceConnectorList from './SourceConnectorList';
import useSourceConnectorPrefs from '@/hooks/useSourceConnectorPrefs';
import { enabledConnectorCount } from './SourceConnectors';

/**
 * The round document button at the left of the composer's controls row. Clicking it pops
 * up the list of sources VoteMate reads on every answer, each locked on.
 *
 * It sits opposite the answer-style control and the send button, in the slot
 * chat products use for "what this assistant can reach". The list is the
 * point: a voter who sees Ballotpedia, Democracy Works, and the Congressional
 * Record named up front knows the answer is grounded before they ask. Nothing
 * in it can be changed today, so the only control is the close.
 *
 * A popover anchored above the button at every width — a full-height sheet
 * would take the whole screen for a five-row list, and on a phone the popover
 * simply fills the width between the 16px gutters. The mobile app renders the
 * same anchored card.
 */
export default function SourcesButton() {
  const { t } = useTranslation('chat');
  const { t: tCommon } = useTranslation('common');
  const analytics = useAnalytics();
  const { disabled } = useSourceConnectorPrefs();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const titleId = useId();
  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    analytics.track('Chat Sources Opened', { source: 'composer' });
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Tooltip title={t('input.sources.tooltip')}>
        <IconButton
          onClick={handleOpen}
          aria-label={t('input.sources.ariaLabel', { count: enabledConnectorCount(disabled) })}
          aria-haspopup="dialog"
          aria-expanded={open}
          // A soft circle the size of the send button opposite it, in the same
          // surfaceWarm fill as the answer-style pill so the row reads as one
          // set of controls.
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            backgroundColor: semanticColors.surfaceWarm,
            color: semanticColors.textCaption,
            transition: 'background-color 0.2s ease, color 0.2s ease',
            '&:hover': { backgroundColor: primitiveColors.gray3, color: semanticColors.textBody },
            '&:focus-visible': {
              outline: `2px solid ${semanticColors.brandPrimary}`,
              outlineOffset: '2px',
            },
          }}
        >
          <DocumentTextIcon aria-hidden="true" style={{ width: 20, height: 20 }} />
        </IconButton>
      </Tooltip>

      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        // Opens beneath the composer, hanging off the button; MUI keeps it
        // inside the viewport when the composer sits near the bottom edge.
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            // MUI's Popover traps focus while open, so the semantics say so too.
            role: 'dialog',
            'aria-modal': true,
            'aria-labelledby': titleId,
            sx: {
              position: 'relative',
              borderRadius: '12px',
              width: 280,
              maxWidth: 'calc(100vw - 32px)',
              p: 1.25,
              mt: 1,
            },
          },
        }}
      >
        {/* A visible close as well as Escape and click-away: the panel is a
            dialog with nothing else to press, so this is its one control. */}
        <IconButton
          onClick={handleClose}
          aria-label={t('input.sources.dismiss')}
          size="small"
          sx={{ position: 'absolute', top: 4, right: 4, color: semanticColors.textCaption }}
        >
          <XMarkIcon aria-hidden="true" style={{ width: 16, height: 16 }} />
        </IconButton>
        <Box sx={{ pr: 4 }}>
          <Typography
            id={titleId}
            component="h2"
            sx={{
              fontSize: 13,
              fontWeight: 600,
              lineHeight: 1.3,
              color: semanticColors.textPrimary,
            }}
          >
            {tCommon('sourceConnectors.title')}
          </Typography>
        </Box>
        <SourceConnectorList compact surface="composer" />
      </Popover>
    </>
  );
}
